import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { useAlert } from 'contexts/AlertContext';
import './ScheduleVote.css';

interface TimeSlot {
    date: string;
    startTime: string;
    endTime: string;
    availableCount: number;
    voteCount: number;
    proposers: any[];
    voters: string[];
    isRecommended: boolean;
    hasUserVoted: boolean;
}

export const ScheduleVote = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { showAlert } = useAlert();
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [votingSlotKey, setVotingSlotKey] = useState<string | null>(null);

    // 스크롤 최상단으로
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 시간대별 통계 조회
    useEffect(() => {
        const fetchData = async () => {
            if (meetingId) {
                try {
                    setLoading(true);
                    const response = await ScheduleRepository.onGetTimeSlotStatistics(meetingId);
                    console.log('투표 화면 - 시간대별 통계:', response.data);
                    setTimeSlots(response.data?.data?.timeSlots || []);
                } catch (error) {
                    console.error('통계 조회 실패:', error);
                    showAlert({ message: '데이터 조회에 실패했습니다.' });
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [meetingId]);

    // 투표 토글
    const handleVote = async (slot: TimeSlot) => {
        if (!meetingId) return;

        const slotKey = `${slot.date}-${slot.startTime}`;
        setVotingSlotKey(slotKey);

        try {
            await ScheduleRepository.onPostVoteTimeSlot({
                meetingId,
                date: slot.date,
                startTime: slot.startTime.substring(0, 5) // HH:mm:ss -> HH:mm
            });

            // 투표 후 데이터 다시 조회
            const response = await ScheduleRepository.onGetTimeSlotStatistics(meetingId);
            setTimeSlots(response.data?.data?.timeSlots || []);

            showAlert({
                message: slot.hasUserVoted ? '투표가 취소되었습니다.' : '투표가 완료되었습니다.'
            });
        } catch (error) {
            console.error('투표 실패:', error);
            showAlert({ message: '투표 처리에 실패했습니다.' });
        } finally {
            setVotingSlotKey(null);
        }
    };

    // 날짜 포맷팅 (YYYY-MM-DD → MM월 DD일)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    // 요일 표시
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return days[date.getDay()];
    };

    if (loading) {
        return (
            <div className="schedule-vote-wrapper">
                <Header title="일정 투표" isSubPage />
                <div className="schedule-vote-content">
                    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                        로딩 중...
                    </div>
                </div>
            </div>
        );
    }

    if (timeSlots.length === 0) {
        return (
            <div className="schedule-vote-wrapper">
                <Header title="일정 투표" isSubPage />
                <div className="schedule-vote-content">
                    <div className="empty-vote-state">
                        <div className="empty-vote-icon">📅</div>
                        <h3 className="empty-vote-title">제안된 일정이 없습니다</h3>
                        <p className="empty-vote-description">
                            먼저 가능한 일정을 등록해주세요
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="schedule-vote-wrapper">
            <Header title="일정 투표" isSubPage />

            <div className="schedule-vote-content">
                {/* 안내 문구 */}
                <div className="vote-description">
                    <div className="vote-description-icon">🗳️</div>
                    <div className="vote-description-text">
                        <div className="vote-description-title">참여 가능한 시간에 투표하세요</div>
                        <div className="vote-description-subtitle">
                            여러 시간대에 투표할 수 있습니다
                        </div>
                    </div>
                </div>

                {/* 투표 목록 */}
                <div className="vote-list">
                    {timeSlots.map((slot, index) => {
                        const slotKey = `${slot.date}-${slot.startTime}`;
                        const isVoting = votingSlotKey === slotKey;

                        return (
                            <div
                                key={index}
                                className={`vote-item ${slot.hasUserVoted ? 'voted' : ''} ${slot.isRecommended ? 'recommended' : ''}`}
                            >
                                <div className="vote-item-header">
                                    <div className="vote-item-date">
                                        {formatDate(slot.date)} ({getDayOfWeek(slot.date)})
                                    </div>
                                    {slot.isRecommended && (
                                        <div className="vote-recommend-badge">추천</div>
                                    )}
                                </div>

                                <div className="vote-item-time">
                                    {slot.startTime.substring(0, 5)} ~ {slot.endTime.substring(0, 5)}
                                </div>

                                <div className="vote-item-info">
                                    <div className="vote-item-stats">
                                        <span className="vote-stat">
                                            <span className="vote-stat-icon">👤</span>
                                            {slot.availableCount}명 제안
                                        </span>
                                        <span className="vote-stat">
                                            <span className="vote-stat-icon">👍</span>
                                            {slot.voteCount}표
                                        </span>
                                    </div>

                                    <button
                                        className={`vote-button ${slot.hasUserVoted ? 'voted' : ''}`}
                                        onClick={() => handleVote(slot)}
                                        disabled={isVoting}
                                    >
                                        {isVoting ? '처리중...' : slot.hasUserVoted ? '투표 취소' : '투표하기'}
                                    </button>
                                </div>

                                {/* 제안자 표시 */}
                                {slot.proposers && slot.proposers.length > 0 && (
                                    <div className="vote-item-proposers">
                                        <span className="proposers-label">제안자:</span>
                                        {slot.proposers.map((proposer, idx) => (
                                            <span key={idx} className="proposer-name">
                                                {proposer.nickname}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* 안내 메시지 */}
                <div className="vote-info-box">
                    <div className="vote-info-icon">💡</div>
                    <div className="vote-info-text">
                        투표는 언제든지 변경할 수 있습니다
                    </div>
                </div>
            </div>
        </div>
    );
};
